import { useMemo } from "react";

import type { SourcePreviewResponse, SourcePreviewSchemaField } from "@prequel/import-react";
import {
  Button,
  ButtonStyle,
  Dropdown,
  type DropdownListItem,
  SectionExpandable,
  Spinner,
  Toggle,
} from "@prequel/shared-components";

import type { DatasetKind } from "../types";
import { isColumnType } from "../utils";

import ColumnCombobox, { type ColumnTag, type ImportColumnOption } from "@/components/import/ColumnCombobox";
import { Divider, FormFooter, FormSection } from "@/components/import/FormLayout";
import PreviewTable from "@/components/import/PreviewTable";

type StepTwoProps = {
  createDatasetPending: boolean;
  datasetKind: DatasetKind;
  isEnabled: boolean;
  lastModifiedColumn: string;
  onBack: () => void;
  onCancel: () => void;
  onDatasetKindChange: (kind: DatasetKind) => void;
  onFrequencyChange: (frequency: string) => void;
  onLastModifiedColumnChange: (column: string) => void;
  onPrimaryKeyColumnChange: (column: string) => void;
  onRefreshSample: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onSoftDeleteColumnChange: (column: string) => void;
  preview: SourcePreviewResponse;
  previewPending: boolean;
  primaryKeyColumn: string;
  selectedFrequency: DropdownListItem<string>;
  softDeleteColumn: string;
  syncFrequencyItems: DropdownListItem<string>[];
};

const StepTwo = ({
  createDatasetPending,
  datasetKind,
  isEnabled,
  lastModifiedColumn,
  onBack,
  onCancel,
  onDatasetKindChange,
  onFrequencyChange,
  onLastModifiedColumnChange,
  onPrimaryKeyColumnChange,
  onRefreshSample,
  onSetEnabled,
  onSoftDeleteColumnChange,
  preview,
  previewPending,
  primaryKeyColumn,
  selectedFrequency,
  softDeleteColumn,
  syncFrequencyItems,
}: StepTwoProps) => {
  const timestampColumns = useMemo(() => preview.schema.filter((field) => isColumnType(field, "timestamp")), [preview]);
  const booleanColumns = useMemo(() => preview.schema.filter((field) => isColumnType(field, "boolean")), [preview]);

  return (
    <>
      <FormSection title="Configure change detection">
        <PreviewTable preview={preview} />
        <Button
          type={ButtonStyle.PRIMARY}
          text={
            previewPending ? (
              <span className="flex items-center">
                <Spinner.Inline className="mr-3 text-white" />
                Refreshing...
              </span>
            ) : (
              "Refresh sample"
            )
          }
          onClick={onRefreshSample}
          disabled={previewPending}
        />
      </FormSection>

      <Divider />

      <FormSection title="Type">
        <DatasetTypeSelector selectedKind={datasetKind} setSelectedKind={onDatasetKindChange} />
      </FormSection>

      <Divider />

      <FormSection
        title="System Fields"
        description="Configure fields used for sync management (not mapped to target endpoint fields)."
      >
        <div className="grid gap-6">
          <ColumnPicker
            label="Unique Row Identifier"
            fields={preview.schema}
            selectedColumn={primaryKeyColumn}
            setSelectedColumn={onPrimaryKeyColumnChange}
            tags={[
              { text: "string", className: "bg-gray-100 text-gray-700" },
              { text: "Required", className: "bg-red-50 text-red-700" },
            ]}
            helperText="Primary key for deduplication"
            required
          />
          <ColumnPicker
            label="Last Updated At"
            fields={timestampColumns}
            selectedColumn={lastModifiedColumn}
            setSelectedColumn={onLastModifiedColumnChange}
            tags={[
              { text: "timestamp", className: "bg-gray-100 text-gray-700" },
              { text: "Optional", className: "bg-gray-100 text-gray-600" },
            ]}
            helperText="Timestamp when record was last updated. If provided, may improve sync performance"
            optional
          />
          <ColumnPicker
            label="Soft Delete Flag"
            fields={booleanColumns}
            selectedColumn={softDeleteColumn}
            setSelectedColumn={onSoftDeleteColumnChange}
            tags={[
              { text: "boolean", className: "bg-gray-100 text-gray-700" },
              { text: "Optional", className: "bg-gray-100 text-gray-600" },
            ]}
            helperText="Boolean value indicating a row should be deleted. If not provided, deleted rows will not be detected automatically."
            optional
          />
        </div>
      </FormSection>

      <Divider />

      <FormSection title="Sync Frequency & Advanced Options">
        <Dropdown
          label="Sync Frequency"
          items={syncFrequencyItems}
          selectedItem={selectedFrequency}
          setSelectedItem={(item: DropdownListItem<string>) => onFrequencyChange(item.key)}
        />
        <SectionExpandable label="Advanced options" transparent>
          <Toggle
            label="Enabled"
            checked={isEnabled}
            setChecked={onSetEnabled}
            subtext="When enabled, this dataset will be synced on its configured schedule."
          />
        </SectionExpandable>
      </FormSection>

      <FormFooter>
        <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={onCancel} />
        <div className="flex gap-3">
          <Button type={ButtonStyle.TERTIARY} text="Back" onClick={onBack} disabled={createDatasetPending} />
          <Button
            type={ButtonStyle.PRIMARY}
            submit
            disabled={!primaryKeyColumn || createDatasetPending}
            text={
              createDatasetPending ? (
                <span className="flex items-center">
                  <Spinner.Inline className="mr-3 text-white" />
                  Creating...
                </span>
              ) : (
                "Create Dataset"
              )
            }
          />
        </div>
      </FormFooter>
    </>
  );
};

const DatasetTypeSelector = ({
  selectedKind,
  setSelectedKind,
}: {
  selectedKind: DatasetKind;
  setSelectedKind: (kind: DatasetKind) => void;
}) => {
  const options: {
    kind: DatasetKind;
    label: string;
    description: string;
    disabled?: boolean;
  }[] = [
    {
      kind: "dimension",
      label: "Dimension",
      description: "Dimension tables usually contain entities. Data in each row may change over time.",
    },
    {
      kind: "fact",
      label: "Fact",
      description:
        'Fact tables usually contain events or transactions. Data in each row does not change over time (the data is "append only").',
      disabled: true,
    },
  ];

  return (
    <div className="grid gap-3">
      {options.map((option) => {
        const selected = selectedKind === option.kind;
        return (
          <button
            key={option.kind}
            type="button"
            disabled={option.disabled}
            onClick={() => setSelectedKind(option.kind)}
            className={`
              flex w-full gap-3 rounded-md border p-4 text-left transition-colors
              ${selected ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white"}
              ${option.disabled ? "cursor-not-allowed opacity-50" : "hover:border-emerald-300"}
            `}
          >
            <span
              className={`
                mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border
                ${selected ? "border-emerald-600 bg-emerald-600" : "border-gray-300 bg-white"}
              `}
            >
              {selected && <span className="size-1.5 rounded-full bg-white" />}
            </span>
            <span>
              <span
                className={`
                  block text-sm font-medium
                  ${selected ? "text-emerald-800" : "text-gray-900"}
                `}
              >
                {option.label}
              </span>
              <span className="mt-1 block text-sm text-gray-500">{option.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

type ColumnPickerProps = {
  label: string;
  fields: SourcePreviewSchemaField[];
  selectedColumn: string;
  setSelectedColumn: (column: string) => void;
  tags: ColumnTag[];
  helperText: string;
  required?: boolean;
  optional?: boolean;
};

const ColumnPicker = ({
  label,
  fields,
  selectedColumn,
  setSelectedColumn,
  tags,
  helperText,
  required = false,
  optional = false,
}: ColumnPickerProps) => {
  const columns = useMemo<ImportColumnOption[]>(
    () =>
      fields.map((field) => ({
        name: field.name,
        prequelType: field.prequel_type,
        rawType: field.raw_type,
      })),
    [fields],
  );

  return (
    <ColumnCombobox
      label={label}
      columns={columns}
      selectedColumn={selectedColumn}
      setSelectedColumn={setSelectedColumn}
      tags={tags}
      helperText={helperText}
      required={required}
      optional={optional}
    />
  );
};

export default StepTwo;
